import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import {
  Drawer,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  IconButton,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Split from "react-split";
import CloseIcon from "@mui/icons-material/Close";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../assets/css/custom-ag-grid.css";
import { genKey } from "../../../utils/utilFunctions";
import { DATA_TYPE } from "../../../constants/datatypes";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { useSelector, useDispatch } from "react-redux";
import { treeActions } from "../../../redux/slices/treeSlice";
import { cloneDeep, result } from "lodash";
import { getUpdatedData as inboundUpdatedData } from "./NewRecord";
import { getUpdatedData as outboundUpdatedData } from "./NewRecordOutbound";
import { eachDeep } from "deepdash-es/standalone";
import QuickAddFromFile from "./QuickAdd/QuickAddFromFile";
import CommonGrid from "../../Repository/CommonGrid";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import CustomizeQuickFieldAdd from "./QuickAdd/CustomizeQuickFieldAdd";
import { fieldTypeOptionsArray, outboundFieldTypeOptionsArray } from "../../../constants/constantList";
import { getParentArray } from "../../ItemProperties/Forms/FieldPropertyForm";
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import DatePattern from "../../ItemProperties/PatternModal/DatePattern";
import { api, useGetDatatypePropertiesQuery } from "../../../redux/api";
import NumberFormatter from "../../ItemProperties/PatternModal/NumberFormatter";
import { lgDocPath } from "../../../constants/languageDocPaths";
import LightTooltip from "../../../commonComponents/LightTooltip";

const drawerWidth = 450;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create(["margin", "padding"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    margin: 0,
    marginLeft: 0, // Initially no margin
    padding: 0,
    ...(open && {
      transition: theme.transitions.create(["marginLeft", "padding"], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth, // Set marginLeft to drawerWidth when open
    }),
  })
);
//custom components
//FIELD TYPE(Dropdown)
export const CustomDropdownEditor = ({ value, setValue, values }) => {
  const selectRef = useRef(null);
  const {t} = useTranslation();

  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.value = value;
    }
  }, [value]);

  if (value === undefined && values.length > 0) {
    value = values[0].value;
  }
  const handleClick = (event) =>{
    event.stopPropagation();
  }
  return (
    <div className="ag-fld-type-cell">
      <select
        className="ag-cell-dropdown"
        ref={selectRef}
        name="FIELDTYPE"
        id="ftDropdown"
        value={value}
        style={{paddingLeft:'10px'}}
        onClick={handleClick}
        onChange={(e) => setValue(e.target.value)}>
        {values.map((option, key) => (
          <option key={key} value={option.value}>
            {t(option.text)}
          </option>
        ))}
      </select>
    </div>
  );
};

//REQUIRED (Checkbox)
export const CustomCheckBoxEditor = (props) => {
  const { value, setValue,colDef } = props
  const { t } = useTranslation();
  return (
    <>
      <FormControlLabel
        sx={{ height: "30px" }}
        className={"width-fit-content"}
        control={
          <Checkbox
            name={colDef.field}
            size={"small"}
            checked={value === 'Y' || value === true}
            onChange={(e, checked) => setValue(checked)}
            style={{paddingLeft:'10px'}}
          />
        }
      />
    </>
  );
};
// NUMERIC text field (custom component to allow only numbers)
export const CustomNumericCellEditor = (props) => {
  const { value,setValue,api} = props;
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const handleChange = (e) => {
    const inputVal = e.target.value;
    // const isNumeric = (value) => /^\d+$/.test(value);
    const isNumeric = (value) => /^[0-9]\d*$/.test(value);

    if (isNumeric(inputVal)) {
      setInputValue(inputVal);
      setValue(inputVal);
    } else {
      if (!isNumeric(inputValue)) {
        setInputValue("");
        setValue("");

      }
    }
  };
  
  const handleBlur = () => {
    const trimmedValue = inputValue?.trim();
    if (/^0\d*$/.test(trimmedValue)) {
      const valueToSet = trimmedValue.replace(/^0+/, "")
      setInputValue(valueToSet);
      setValue(valueToSet)
    }
  
    api.stopEditing();
  };
  
  const handleKeyDown = (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
  
      setInputValue((prevValue) => {
        const newValue = prevValue?.slice(0, -1);
        setValue(newValue);
        return newValue;
      });
    }
  };
  

  useEffect(() => {
    const inputElement = inputRef.current;

    if (inputElement) {
      inputElement.addEventListener("keydown", handleKeyDown);

      return () => {
        inputElement.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, []);
  const handleClick = (event) =>{
    event.stopPropagation();
  }

  return (
    <div className="ag-fld-type-cell" style={{ border: "none" }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        title="Input must be a valid number"
        onClick={handleClick}
        // onBlur={handleBlur}
        style={{ borderBottom: "none",paddingLeft:'10px'}}
        className="ag-cell-dropdown"
        maxLength={9}
      />
    </div>
  );
};


// PATTERN Formatter Component,render based on field type , when we have pattern is columnDefs
export const PatternCellEditor = (params) => {
  const rowData = params.data;
  const { value, setValue } = params;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const queryParams = useSelector((state) => state.queryParams);
  const [datePatternModal, setDatePatternModal] = useState(false);
  const [datePattern, setDatePattern] = useState("");
  const [valid, setValid] = useState(true);
  const [dpNetworkCallMessage, setDpNetworkCallMessage] = useState("");
  const [flag, setFlag] = useState(false);
  const [date, setDate] = useState("");
  const [numberFormatterModal, setNumberFormatterModal] = useState(false);
  const [arg, setArg] = useState([]);
  const [pattern, setPattern] = useState("");
  const [treeData, setTreeData] = useState([]);
  const [index, setIndex] = useState(0);
  const [numberPattern, setNumberPattern] = useState("");
  const [loading, setLoading] = useState(false);

  const docPaths = {
    DATE: `${queryParams.baseUrl}/docs/${
      lgDocPath[queryParams.lang] || "en"
    }/Workbench/dialogs/DateFormatter.html`,
    NumberFormatter: `${queryParams.baseUrl}/docs/${
      lgDocPath[queryParams.lang] || "en"
    }/Workbench/dialogs/NumberFormatter.html`,
  };
  const handleBtnEnable = () => {
    if (
      rowData.fieldType === "DATE" ||
      rowData.fieldType === "FLOAT" ||
      rowData.fieldType === "INTEGER"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const isBtnDisabled = !handleBtnEnable();

  const handleClick = () => {
    if (rowData.fieldType === "DATE") {
      setDatePatternModal(true);
    } else if (rowData.fieldType === "FLOAT" || rowData.fieldType === "INTEGER") {
      setNumberFormatterModal(true);
      setNumberPattern(pattern);
    }
  };
  const datePatternModalOkHandler = () => {
    setDate(datePattern);
    setValue(datePattern);
    setDatePatternModal(!datePatternModal);
  };

  const datePatternModalCancelHandler = () => {
    setDatePatternModal(false);
  };
  const testDateHandler = (key, val) => {
    if (!val) {
      setDpNetworkCallMessage(
        t(
          "BOX_NO_FORMAT_WAS_SPECIFIED__SEL_CANCEL_TO_CLS_DIALOG_AND_RET_TO_MAIN_SCREEN"
        )
      );
      setValid(false);
    }
    if (val) {
      setLoading(true);
      dispatch(
        api.endpoints.validateDatePattern.initiate({
          datePattern: encodeURIComponent(val),
        })
      ).then((response) => {
        if (response) {
          if (response.data.current_state === "ERROR") {
            setValid(false);
            setFlag(false);
            setDpNetworkCallMessage(response.data.message);
          } else {
            if (key === "fromModal") {
              setFlag(true);
              setDpNetworkCallMessage(response.data.message)
            }
            setValid(true);
          }
        }
      }).finally(() => {
        setLoading(false);
      })
    }
  };
  const numberFormatterOkHandler = (valuesObj) => {
    const newArgArray = cloneDeep(arg);
    newArgArray[index] = {
      ...newArgArray[index],
      ...valuesObj,
    };
    setArg(newArgArray);
    setPattern(valuesObj?.PATTERN);
    setNumberFormatterModal(false);
  };
  const handleTextFldClick = (event) =>{
    event.stopPropagation();
  }
  return (
    <>
      <div style={{ display: "flex", boxSizing: "border-box" }}>
        <div
          className="ag-fld-type-cell"
          style={{
            border: "none",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 1,
            flexGrow: 1,
          }}>
          <input
            type="text"
            value={
              value ||
              (rowData.fieldType === "DATE" ? date || datePattern : pattern)
            }
            onChange={(e) => {
              setValue(e.target.value);
            }}
            onClick={handleTextFldClick}
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              borderRadius: "0",
              paddingLeft:'10px'
            }}
          />
          <button
            type="button"
            onClick={handleClick}
            disabled={isBtnDisabled}
            style={{
              backgroundColor: "transparent",
              border: "none",
              height: "20px",
              width: "20px",
              cursor: "pointer",
            }}>
            {rowData.fieldType === "DATE" ? (
              <div style={{marginLeft: "-5px"}} className="CALENDAR_MONTH"></div>
            ) : rowData.fieldType === "FLOAT" ||
              rowData.fieldType === "INTEGER" ? (
              <div style={{marginLeft: "-5px"}} className="CALCULATE"></div>
            ) : (
              <span style={{ border: "solid 1px #ddd" }}>...</span>
            )}
          </button>
        </div>
      </div>
      {datePatternModal && (
        <DatePattern
          openModal={datePatternModal}
          docPath={docPaths.DATE}
          okHandler={datePatternModalOkHandler}
          cancelHandler={datePatternModalCancelHandler}
          onChangeHandler={setDatePattern}
          testDateHandler={testDateHandler}
          datePattern={datePattern}
          dpNetworkCallMessage={dpNetworkCallMessage}
          valid={valid}
          loading={loading}
        />
      )}
      {numberFormatterModal && (
        <NumberFormatter
          openModal={numberFormatterModal}
          docPath={docPaths.NumberFormatter}
          cancelHandler={setNumberFormatterModal}
          okHandler={numberFormatterOkHandler}
          fieldValue={numberPattern}
        />
      )}
    </>
  );
};
//HL7 data type dropdown
export const HL7DataTypeDropdown = ({ value, setValue, values }) => {
  const selectRef = useRef(null);
  useEffect(() => {
    if (selectRef.current) {
      selectRef.current.value = value;
    }
  }, [value]);

  if (value === undefined && values.length > 0) {
    value = values[0];
  }
  return (
    <div className="ag-fld-type-cell">
      <select
        className="ag-cell-dropdown"
        ref={selectRef}
        name="HL7DATATYPE"
        id="dtDropdown"
        value={value}
        style={{paddingLeft:'10px'}}
        onChange={(e) => setValue(e.target.value)}>
        {values.map((option, key) => (
          <option key={key} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
};

export const CustomTextInputRenderer = (props) => {
  const { value, data, setValue, api,colDef } = props;
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const inputValueRef = useRef(inputValue);

  const handleChange = (e) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);
    setValue(inputVal);
  };
  const handleBlur = () => {
    if (colDef.field === "description") {
      const newValue = inputValue.trim() !== "" ? inputValue : data.mapName || "";

      if (newValue !== value) {
        setInputValue(newValue);
        setValue(newValue)
        api.stopEditing();
      }
    }
     else if(colDef.field === 'constVal') {
      if (data && data.constVal && data.constVal !== "") {
        data.fieldType = 'CONSTANT';
        api.refreshCells({ rowNodes: [api.getRowNode(data.id)] });
      }
    }
  };


  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Backspace') {
        e.preventDefault();

        inputValueRef.current = inputValueRef.current?.slice(0, -1) || '';
        setValue(inputValueRef.current);
      }
    };

    const inputElement = inputRef.current;

    if (inputElement) {
      inputElement.addEventListener('keydown', handleKeyDown);

      return () => {
        inputElement.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [setValue]);
  
  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  const handleClick = (event) =>{
    event.stopPropagation();
  }
  return (
    <div className="ag-fld-type-cell" style={{ border: "none" }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleClick}
        style={{ borderBottom: "none",paddingLeft:'10px' }}
        className="ag-cell-dropdown"
      />
    </div>
  );
};

const CustomAutocompleteCellRenderer = (props) => {
  const { value, colDef, api, node, data, values, setValue } = props;
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const handleChange = (event, newValue) => {
    setInputValue(newValue);
    setValue(newValue)
    api.stopEditing();
    // Update the data in the row to reflect the new value
    data[colDef.field] = newValue;
    api.applyTransaction({ update: [data] });
  };


  return (
      <Autocomplete
        value={inputValue}
        size="small"
        onChange={handleChange}
        options={values}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            autoComplete="off"
            fullWidth
            value={inputValue}
            InputProps={{
              ...params.InputProps,
              classes: { root: 'no-border-fieldset' },
            }}
            onChange={(e) => {
              setInputValue(e.target.value);
              setValue(e.target.value);
            }}
          />
        )}
        onBlur={() => api.stopEditing()}
        freeSolo
        fullWidth
        style={{border:'none !important'}}
        className="ag-cell-dropdown ag-fld-type-cell"
      />
      
  );
};

// QuickAddModal component
function QuickAddModal(props) {
  const { openModal, toggle } = props;
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState(null);
  const [openFileModal, setOpenFileModal] = useState(false);
  const [hasDuplicateFieldName, setHasDuplicateFieldName] = useState(false);
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree } = useSelector((state) => state.treeData[parentScreen]);
  const currentTab = rightTree.currentTab;
  const translatedData = rightTree.tree[currentTab].translatedData;
  const selectedNode = rightTree.tree[currentTab].selectedNode;
  const templateHeader = rightTree.tree.templateHeader;
  const { account } = useSelector((state) => state.queryParams);
  const { currentScreen } = useSelector((state) => state.treeData);
  const dispatch = useDispatch();
  const title = t("DIAG_QUICK_FIELD_ADD_DESC").split("\\n");
  const [enableAdd, setEnableAdd] = useState(false);
  const [deletedKeys, setDeletedKeys] = useState([]);
  const [gridKey, setGridKey] = useState(0);
  const [customizePasteLogic, setCustomizePasteLogic] = useState({});
  const [pasteLogicUpdated,setPasteLogicUpdated] = useState(false)
  const [tabData, setTabData] = useState({});
  const [hl7DatatypeDropdownItems, sethl7DatatypeDropdownItems] = useState([
    t("SELECT"),
  ]);
  const hl7DatatypeOptions = useGetDatatypePropertiesQuery({
    account: account,
    report_type: "hl7",
    version: templateHeader?.FILETYPE_VERSION,
  });
  useEffect(() => {
    sethl7DatatypeDropdownItems([
      t("SELECT"),
      ...(hl7DatatypeOptions.data?.data || []),
    ]);
  }, [hl7DatatypeOptions]);

  const [rowData, setRowData] = useState([
    {
      id: 0,
      mapName: "",
      description: "",
      fieldType: fieldTypeOptionsArray[0].value,
      length: "",
      minLength: "",
      maxLength: "",
      req: false,
      pattern: "",
      precision: "",
      constVal: "",
      hl7DataType: hl7DatatypeDropdownItems[0],
      convertCase: "",
      paddingDirection:"",
      addToworkflowSession:"",
      quoteData:false,
      trimData:false,
      urlEncodeData:false,
      suppressFieldDelimiter:false
    },
  ]);
  const [data, setData] = useState("");
  const [delimiter, setDelimiter] = useState("CRLF");
  const [confirmModal, setConfirmModal] = useState(false);
  const [enableResetGrid, setenableResetGrid] = useState(false);
  const [columnDefs,setColumnDefs] = useState([])
  const [gridOptions, setGridOptions] = useState({
    columnDefs: columnDefs,
  });

  const [fieldNameDuplicateMsg, setFieldNameDuplicateMsg] = useState("");
  const [customizeDialog, setCustomizeDialog] = useState(false);
  const [availableColumns, setAvailableColumns] = useState({});
  const columnMapping = {
    req: t("LBL_REQUIRED"),
    minLength: t("LBL_MIN_LENGTH"),
    maxLength: t("LBL_MAX_LENGTH"),
    pattern: t("LBL_PATTERN"),
    precision: t("LBL_PRECISION"),
    constVal: t("LBL_CONST_VAL"),
    length: t("LBL_LENGTH"),
    hl7DataType: t("LBL_HL7_DATA_TYPE"),
    convertCase: t('LBL_CONVERT_CASE'),
    paddingDirection: t('LBL_PADDING_DIRECTION'),
    fillCharacter: t('LBL_FILL_CHAR'),
    quoteData: t('BTN_QUOTE_DATA'),
    trimData: t('BTN_TRIM_DATA'),
    urlEncodeData: t('BTN_URLENCODE_DATA'),
    suppressFieldDelimiter: t('LBL_SUPP_FLD_DELIM'),
    addToWorkflowSession: t('BTN_WS_USE_IN_SESSION')
  };

  const convertCaseDropdownItems = [
    {value:'DO_NOT_CONVERT',text:t('DO_NOT_CONVERT')},
    {value:'UPPER_CASE',text:t('UPPER_CASE')},
    {value:'LOWER_CASE',text:t('LOWER_CASE')},
    {value:'PROPER_CASE',text:t('PROPER_CASE')},
  ]

  const paddingDirectionDropdownItems = [
    { value: "LEFT", text: t("LEFT") },
    { value: "RIGHT", text: t("RIGHT") },
  ];

  const fillCharacterDropdownItems = ["SPACE"]
  // Retrieve the saved paste logic through the API.
  const getCustomizePasteLogic = () => {
    if (openModal) {
      setIsLoading(true);
      dispatch(
        api.endpoints.processQuickAddCustomizeScreen.initiate(
          {
            template_type: currentScreen === "inbound" ? "in" : "out",
            hl7: templateHeader.FILETYPE === "HL7" ? "Y" : "N",
            data: "",
          },
          { forceRefetch: true }
        )
      )
        .then((response) => {
          if (response?.data?.current_state === "SUCCESS") {
            const { availableColumns, delimiter } = response.data.data;
            const tabInfo = response.data.data;
            dispatch(
              treeActions.setQuickAdd({
                data: {
                  recordDelim: delimiter.recordDelimter,
                  fieldDelim: delimiter.fieldDelimiter,
                },
              })
            );
            setAvailableColumns(availableColumns);
            setTabData(tabInfo);
            setTimeout(() => setIsLoading(false), 1000);
          } else {
            console.log(response.message);
          }
        })
        .catch((err) => {
          setIsLoading(false);
        });
    }
  };
  useEffect(() => {
    setRowData([
      {
        id: 0,
        mapName: "",
        description: "",
        fieldType: fieldTypeOptionsArray[0].value,
        req: false,
        length: "",
        minLength: "",
        maxLength: "",
        constVal: "",
        pattern: "",
        precision: "",
        hl7DataType: hl7DatatypeDropdownItems[0],
        constVal: "",
        convertCase: "",
        paddingDirection:"",
        addToworkflowSession:"",
        quoteData:false,
        trimData:false,
        urlEncodeData:false,
        suppressFieldDelimiter:false
      },
    ]);
    if (openModal){
      getCustomizePasteLogic();
    }

    if (gridApi && gridOptions.columnDefs) {
      handleHL7DataType(availableColumns);
    }
  }, [openModal]);
  
  useEffect(()=>{
    if (pasteLogicUpdated){
      setIsLoading(true)
       setTimeout(() => {
        getCustomizePasteLogic();
       }, 1000);
    }
  },[pasteLogicUpdated]);

  const handleHL7DataType = (columns) => {
    if (columns) {
      const columnsToRemove = [];

      for (const column in columns) {
        if (
          column === "hl7DataType" &&
          columns[column] === true &&
          templateHeader.FILETYPE !== "HL7"
        ) {
          columnsToRemove.push(columnMapping[column]);
        }
      }
      const filteredColumnDefs = columnDefs.filter((colDef) => {
        return !columnsToRemove.includes(colDef.headerName);
      });

      // Update the columnDefs with the filtered columns
      setColumnDefs(filteredColumnDefs);
    }
  };
  useEffect(() => {
    setenableResetGrid(rowData.length > 1);
  }, [rowData]);

  useEffect(() => {
    if (Object.keys(availableColumns).length > 0) {
      updateGridOptions(availableColumns);
    }
  }, [availableColumns]);

  useEffect(() => {
    // Set gridOptions when columnDefs change
    setGridOptions((prevGridOptions) => ({
      ...prevGridOptions,
      columnDefs,
    }));
  }, [columnDefs]);

  useEffect(() => {
    const updatedColumnDefs = [
      {
        showDeleteIconOnly: true,
        isDeleteControl: true,
        editable: false,
        suppressMovable: true,
        lockPosition: true,
        deleteHandler: (data) => {
          setEnableAdd(true);
          if (data?.initalData) {
            setDeletedKeys((prev) => [...prev, data]);
          }
        },
        showDeleteIcon: (data) => {
          return (
            data.mapName ||
            data.description ||
            (data.fieldType && data.fieldType !== fieldTypeOptionsArray[0].value) ||
            data.pattern ||
            data.precision ||
            data.length ||
            data.minLength ||
            data.maxLength ||
            (data.hl7DataType && data.hl7DataType !== hl7DatatypeDropdownItems[0]) ||
            data.req === true ||
            data.req === "Y" ||
            data.constVal ||
            data.convertCase ||
            data.fillCharacter ||
            data.paddingDirection ||
            data.trimData === true ||
            data.trimData === "Y" ||
            data.urlEncodeData === true ||
            data.urlEncodeData === "Y" ||
            data.addToWorkflowSession ||
            data.quoteData === true ||
            data.quoteData === 'Y' ||
            data.suppressFieldDelimiter === true ||
            data.suppressFieldDelimiter === "Y"
          );
        },
        headerClass: "first-column-header",
      },
      {
        headerName: t("LBL_FLD_NAME"),
        field: "mapName",
        editable: true,
        resizable: true,
        showPlaceholder: false,
        cellRenderer: CustomTextInputRenderer,
        width: 305,
        lockVisible: true,
        hide: currentScreen === 'outbound',
      },
      {
        headerName: t("LBL_DESC"),
        field: "description",
        editable: true,
        resizable: true,
        showPlaceholder: false,
        cellRenderer: CustomTextInputRenderer,
        width: 305,
        lockVisible: true,
      },
      {
        headerName: t("LBL_FLD_TYPE"),
        field: "fieldType",
        resizable: true,
        cellRenderer: CustomDropdownEditor,
        cellRendererParams: { values: currentScreen === 'outbound' ? outboundFieldTypeOptionsArray : fieldTypeOptionsArray },
        cellStyle: { margin: 0, padding: 0 },
        width: 150,
        lockVisible: true,
      },
    ];

    setColumnDefs(updatedColumnDefs);
  }, [currentScreen]);


  useEffect(() => {
    //Update the grid column definitions when data changes
    if (gridApi && gridOptions.columnDefs) {
      gridApi.setGridOption("columnDefs", gridOptions.columnDefs);
      setGridKey((prevKey) => prevKey + 1);
    }
  }, [gridOptions]);

  const handleCellValueChanged = (gridOptions) => {
    const getMappedValue = (value, mappingArray = [], key) => {
      const KEYMAPPING = {
        required :"chkVal",
        pattern : "pattern",
        fieldType: "fieldType",
        constVal: "constVal",
      }
      for (let i = 0; i < mappingArray?.length; i++) {
        const val = mappingArray[i]?.value;
        if (val === value) {
          return {
            found : true,
            value :mappingArray?.[i]?.[KEYMAPPING[key]]
          }
        }
      }
      return {
        found : false,
        value : value
      }
    };
    if (gridOptions.newValue !== gridOptions.oldValue) {
      setEnableAdd(false);
      setenableResetGrid(true);
    }
    const colId = gridOptions.column.colId;
    if (colId === "pattern" || colId === "constVal"){
      const changedValue = gridOptions.data[colId];
      const {value, found} = getMappedValue(changedValue, tabData[colId], colId);
      if (found){
        const rowNode = gridOptions.api.getDisplayedRowAtIndex(gridOptions.rowIndex);
        rowNode.setDataValue(colId, value);
        colId === 'pattern' ? rowNode.setDataValue('fieldType', 'DATE') : rowNode.setDataValue('fieldType', 'CONSTANT') ;
      }
    }
  };

  // Function to add nodes to the tree
  const handleAdd = (node, selectedNode, isInside,position) => {
    const parentKey = !isInside
      ? selectedNode.key.split("-").slice(0, -1).join("-")
      : selectedNode.key;
    const newTranslatedData = cloneDeep(translatedData);
    const newPos =selectedNode.key.split("-");
    const lastIndex = newPos[newPos.length - 1]
    
    eachDeep(newTranslatedData, (ele, index) => {
      if (ele?.key === parentKey) {
        if (isInside) {
          if (position && Array.isArray(position)) {
            position.forEach((pos, i) => {
              if (pos >= 0 && pos <= ele.children.length) {
                ele.children.splice(pos, 0, node[i]);
              }
            });
          } else {
            ele.children = [...node, ...ele.children];
          }
        } else {
          if(!position){
          const insertionIndex = Number(lastIndex) + 1;
           ele.children.splice(insertionIndex, 0, ...node);
          }
          else{
            position.forEach((pos, i) => {
              if (pos >= 0 && pos <= ele.children.length) {
                ele.children.splice(pos, 0, node[i]);
              } 
            });
          }
        }
      }
    });

    const lastNode = node[node.length - 1];

    // Update the key and fieldNumber of the last node
    if (isInside) {
        let newKey = selectedNode.key.split("-");
        position
          ? newKey.push(position[position.length - 1])
          : newKey.push(node.length - 1);
        newKey = newKey.join("-");
        lastNode.key = newKey;
        lastNode.fieldNumber = `${
          templateHeader?.FILETYPE === "EDI"
            ? selectedNode?.ICON_DISPLAY.includes("FIELD_GROUP")
              ? position ? position[position.length-1] +1:node.length
              : position ?position[position.length-1] :node.length - 1
            : position ? position[position.length-1] +1:node.length
        }`;
    } else {
        let newIdx = position  ? (position[position.length -1]) : (Number(lastIndex) + node.length);
        let newKey = selectedNode.key.split("-");
        newKey.pop();
        newKey.push(newIdx);
        newKey = newKey.join("-");
        lastNode.key = newKey;
        lastNode.fieldNumber = templateHeader?.FILETYPE === "EDI" ? newIdx : newIdx + 1;
      }
       // Dispatching Empty Node for unmounting middle panel and then mounting again
       dispatch(treeActions.setRightTreeSelectedNode({
        screen: parentScreen,
        type: currentTab,
        data: {}
      }));
     
      // Dispatch actions to update the tree and translated data
      setTimeout(() => {
        dispatch(
        treeActions.setRightTreeSelectedNode({
          screen: parentScreen,
          type: currentTab,
          data: lastNode,
        })
      )
    });
    const updatedData =
      currentScreen === "inbound"
        ? inboundUpdatedData(newTranslatedData, templateHeader?.FILETYPE)
        : outboundUpdatedData(newTranslatedData, templateHeader?.FILETYPE, null, parentScreen === 'workflow', currentTab);
    dispatch(
      treeActions.setNewRightTreeTranslatedData({
        screen: parentScreen,
        type: currentTab,
        data: updatedData,
      })
    );
  };
  const handleAddFields = () => {
    function checkDuplicateDescNodeInTranslatedData(node, parentArray) {
      for (let i = 0; i < parentArray.length; i++) {
        if (
          parentArray[i].ICON_DISPLAY === "RECORD" ||
          parentArray[i].ICON_DISPLAY === "RECORD_TOP"
        ) {
          continue;
        }

        if (
          currentScreen === "outbound" &&
          node.description &&
          node.description.toLowerCase() ===
            parentArray[i]?.DESCRIPTION?.toLowerCase()
        ) {
          return { result: true };
        }
      }
      return { result: false };
    }
    const parentArray =
    selectedNode.ICON_DISPLAY === "RECORD" ||
    selectedNode.ICON_DISPLAY === "RECORD_TOP" ||
    selectedNode.ICON_DISPLAY === 'CONDITION_IF'
      ? selectedNode.children
      : getParentArray(
          selectedNode.key.split("-").slice(0, -1).join("-"),
          translatedData
        );
    const lblFldNames = new Set();
    const uniqueDesc = new Set();
    let isUnique = true;
    let mapNameExist = true;
    let rowData = [];
    gridApi.forEachNode((node) => {
      rowData.push(node.data);
    });
    rowData.filter((record)=>!(Object.keys(record).length === 1 && record.hasOwnProperty('id')))
    .forEach((record) => {
      const lblFldName = record.mapName;
      const desc = record.description;
      const hasEmptyString = Object.keys(record).some((key) => {
        return key !== "id" && record[key] === "";
      });
      if(currentScreen === 'inbound'){
        if (lblFldName) {
          const lowercaseName = lblFldName.toLowerCase();
          if (lblFldNames.has(lowercaseName)) {
            isUnique = false;
            return;
          }
          lblFldNames.add(lowercaseName);
        }

        else if((!lblFldName && record.description!=='' && !hasEmptyString)){
          mapNameExist = false;
        }
      }
      if(currentScreen === 'outbound'){
        if (desc) {
          const lowercaseDesc = desc.toLowerCase();
          if (uniqueDesc.has(lowercaseDesc)) {
            isUnique = false;
            return;
          }
          uniqueDesc.add(lowercaseDesc);
        }
      }
    });
   
    const isValidNumber = (value) => /^\d+$/.test(value);
    const validateNumericField = (fieldName, errorMsg) => {
      const hasInvalidValue = rowData.some((record) => {
        if (record[fieldName]) {
          const isNumeric = isValidNumber(record[fieldName]);
          return !isNumeric;
        }
        return false;
      });

      if (hasInvalidValue) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(errorMsg);
        return true;
      }

      return false;
    };
    const validateMaxLength = () =>{
      const hasInvalidLength = rowData.some((record) => {
        const maxLength = record.maxLength;
        const minLength = record.minLength;
        if (maxLength && minLength && Number(maxLength) < Number(minLength)) {
            return true;
        }
    
        return false;
    });
    
      if(hasInvalidLength) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(t('BOX_MSG_MAX_LENGTH_VALIDATION'))
        return true;
      }
      return false;
    }
    function handleInputLimit(fieldName,errorMsg) {
      const isValidInput = (value) => value.length <= 9;
      const hasInvalidValue = rowData.some((record) => {
        if (record[fieldName]) {
          const isValid = isValidInput(record[fieldName]);
          return !isValid;
        }
        return false;
      });

      if (hasInvalidValue) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(errorMsg);
        return true;
      }

      return false;
    }
    const invalidMaxLength = validateMaxLength();
    const hasInvalidPrecision = validateNumericField(
      "precision",
      t("BOX_PRECISION_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidMaxLength = validateNumericField(
      "maxLength",
      t("BOX_MAX_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidMinLength = validateNumericField(
      "minLength",
      t("BOX_MIN_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidLength = validateNumericField(
      "length",
      t("BOX_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidPrecisionCharLimit = handleInputLimit("precision","Precision cannot exceed 9 characters")
    const hasInvalidMaxLengthCharLimit = handleInputLimit("maxLength","Maximum length cannot exceed 9 characters")
    const hasInvalidMinLengthCharLimit = handleInputLimit("minLength","Minimum length cannot exceed 9 characters")
    const hasInvalidLengthCharLimit = handleInputLimit("length","Length cannot exceed 9 characters")

    if (hasInvalidMinLength || hasInvalidMaxLength || hasInvalidPrecision || hasInvalidLength || invalidMaxLength || hasInvalidLengthCharLimit || hasInvalidMaxLengthCharLimit || hasInvalidMinLengthCharLimit || hasInvalidPrecisionCharLimit) {
      return;
    }
    
    if (!isUnique) {
      setHasDuplicateFieldName(true);
      setFieldNameDuplicateMsg(t("BOX_FIELD_NAME_MUST_BE_UNIQUE"));
      return;
    }
    if(!mapNameExist){
      setHasDuplicateFieldName(true);
      setFieldNameDuplicateMsg(t("BOX_FIELD_NAME_IS_REQUIRED").split('.')[0]);
      return;

    }
    const record = [];
    const optionsArray = currentScreen === 'inbound' ? fieldTypeOptionsArray : outboundFieldTypeOptionsArray;

    // Create new nodes from the rowData
    const updatedTranslatedData = rowData
      .filter((record) => {
        if (currentScreen === "outbound") {
          return (
            record.description &&
            !(Object.keys(record).length === 1 && record.hasOwnProperty("id"))
          );
        }
        else return (record.mapName);
      })
      .map((node) => {
       const duplicate = checkDuplicateDescNodeInTranslatedData(node,parentArray)
        if(!duplicate.result){
        const title = node.description || node.mapName;
        const fieldType =
          currentScreen === "outbound" && node.constVal
            ? "CONSTANT"
            : node.fieldType === optionsArray[0].value
            ? "ALPHA"
            : optionsArray.some((o) => node.fieldType === o.value)
            ? node.fieldType
            : "ALPHA";
        const newNode = {
          nodeId: genKey(node.mapName),
          title: `${t("LBL_FIELD")} ${(node.fieldType === "CONSTANT" || node.constVal) ? "(" + t("CONSTANT") + ")" : ""} ${title}`,
          ICON_DISPLAY: DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon,
          type: "FIELD",
          children: [],
          isLeaf: true,
          FIELDTYPE: [
            "FLOAT",
            "INTEGER",
            "PACKED_INT",
            "ZONED_INT",
            "BIT_FLAG",
            "ALPHA",
            "DATE",
            "CONSTANT"
          ].includes(fieldType)
            ? fieldType
            : "ALPHA",
        };
        if(node.mapName && currentScreen === 'inbound'){
          newNode.MAPNAME = node.mapName
        }
        if (node.description || node.mapName) {
          newNode.DESCRIPTION = node.description || node.mapName;
        }
        if ((node.pattern && currentScreen === 'outbound' && node.fieldType !== 'ALPHA') || (node.fieldType === "DATE" && node.pattern && currentScreen === 'inbound')) {
          newNode.PATTERN = node.pattern;
        }
        if(node.pattern && currentScreen === 'outbound' && fieldType === 'ALPHA'){
          newNode.MASK = node.pattern;
        }
        if (
          ["FLOAT", "INTEGER", "PACKED_INT", "ZONED_INT"].includes(
            node.fieldType
          )
        ) {
          newNode.PRECISION = node.precision || 0;
          newNode.ROUND = 'TRUE'
        }
        if (node.maxLength) {
          newNode.MAX_LENGTH = node.maxLength;
        }
        if (node.minLength) {
          newNode.MIN_LENGTH = node.minLength;
        }
        if (node.req && (node.req === "Y" || node.req === true)) {
          newNode.REQUIRED = true;
        }
        if (node.length) {
          newNode.LENGTH = node.length;
        }
        if (node.hl7DataType && templateHeader.FILETYPE === "HL7") {
          newNode.DATA_TYPE =
            node.hl7DataType === hl7DatatypeDropdownItems[0]
              ? "ST"
              : node.hl7DataType;
        }
        if(node.constVal){
          newNode.VALUE = node.constVal
        }
        if(node.trimData && (node.trimData === "Y" || node.trimData === true)){
          newNode.TRIMDATA = 'TRUE'
        }
        if(node.quoteData && (node.quoteData === "Y" || node.quoteData === true)){
          newNode.QUOTE_DATA = 'TRUE'
        }
        if(node.urlEncodeData && (node.urlEncodeData === "Y" || node.urlEncodeData === true)){
          newNode.URLENCODE = 'TRUE'
        }
        if(node.suppressFieldDelimiter && (node.suppressFieldDelimiter === "Y" || node.suppressFieldDelimiter === true)){
          newNode.SUPPRESS_DELIMITER = 'TRUE'
        }
        if(node.addToWorkflowSession){
          newNode.WF_SESSION_NAME = node.addToWorkflowSession
        }
        if(node.convertCase){
          newNode.CONVERT_CASE = node.convertCase
        }
        if(node.paddingDirection){
          newNode.PADDIR = node.paddingDirection
        }
        if(node.fillCharacter){
          newNode.FILLCHAR = node.fillCharacter
        }
        if(node.fieldType === 'INTEGER' && currentScreen === 'outbound'){
          newNode.PRECISION_OUT = "0"
        }
        if(templateHeader?.FILETYPE === 'DATABASE' && [true,'true','TRUE'].includes(templateHeader.INCLUDE_NULL) ) newNode.INCLUDE_NULL = 'TRUE'
        const formattingOptions = ['TRIMDATA', 'QUOTE_DATA', 'URLENCODE'];
        formattingOptions.forEach(condition => {
            if ([true, 'true', 'TRUE'].includes(templateHeader[condition])) {
                newNode[condition] = 'TRUE';
            }
        });
        record.push(newNode);
      }
      });

    const isInside =
      selectedNode.type === "RECORD" ||
      (selectedNode.ICON_DISPLAY === "QUERY" && selectedNode.TYPE !== 'QUERY') ||
      selectedNode.type === "RECORD_TOP" ||
      selectedNode.type === "FIELD_GROUP" ||
      selectedNode.ICON_DISPLAY === "RECORD_LOOP" ||
      selectedNode.ICON_DISPLAY === "RECORD" ||
      selectedNode.ICON_DISPLAY === "CONDITION_IF" ||
      selectedNode.ICON_DISPLAY === "JSON_TOP_RECORD"
    // Add the nodes to the tree
    if (record.length > 0) handleAdd(record, selectedNode, isInside);

    toggle();

    // Clear values for all columns in the rowData
    const updatedRowData = rowData.map((item) => {
      const updatedItem = { id: item.id };

      // Clear values for all columns except the 'id' field
      Object.keys(updatedItem).forEach((key) => {
        if (key === "id") return; // Skip the 'id' field
        updatedItem[key] = "";
      });

      return updatedItem;
    });

    setRowData(updatedRowData);
  };

  const handleCancelClick = (event, reason) => {
    if (reason === "backdropClick") return;
    toggle();
    setOpenFileModal(false);
    setRowData([
      {
        id: 0,
        mapName: "",
        description: "",
        fieldType: fieldTypeOptionsArray[0].text,
        length: "",
      },
    ]);
  };

  const handleAddNew = () => {
    let rowData = [];
    const record = [];
    const isInside =
      selectedNode.type === "RECORD" ||
      selectedNode.ICON_DISPLAY === "QUERY" ||
      selectedNode.type === "RECORD_TOP" ||
      selectedNode.type === "FIELD_GROUP";


    // Fetch data from the grid
    fetchDataFromGrid(rowData);

    const parentArray =
      selectedNode.ICON_DISPLAY === "RECORD" ||
      selectedNode.ICON_DISPLAY === "RECORD_TOP" ||
      selectedNode.ICON_DISPLAY === 'CONDITION_IF'
        ? selectedNode.children
        : getParentArray(
            selectedNode.key.split("-").slice(0, -1).join("-"),
            translatedData
          );
    const hasInvalidPrecision = validateNumericField(
      "precision",
      t("BOX_PRECISION_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidMaxLength = validateNumericField(
      "maxLength",
      t("BOX_MAX_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidMinLength = validateNumericField(
      "minLength",
      t("BOX_MIN_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidLength = validateNumericField(
      "length",
      t("BOX_LENGTH_MUST_BE_VALID_NUMBER").split(".")[0]
    );
    const hasInvalidPrecisionCharLimit = handleInputLimit("precision","Precision cannot exceed 9 characters")
    const hasInvalidMaxLengthCharLimit = handleInputLimit("maxLength","Maximum length cannot exceed 9 characters")
    const hasInvalidMinLengthCharLimit = handleInputLimit("minLength","Minimum length cannot exceed 9 characters")
    const hasInvalidLengthCharLimit = handleInputLimit("length","Length cannot exceed 9 characters")

    const invalidMaxLength = validateMaxLength();

    if (rowData) {
      if (currentScreen === 'inbound' && !checkMapNameExists(rowData)) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(t("BOX_FIELD_NAME_IS_REQUIRED").split(".")[0]);
        return;
      }
      if(currentScreen === 'inbound' && !isUniqueMapNameInGrid(rowData)){
         setHasDuplicateFieldName(true)
         setFieldNameDuplicateMsg(t('BOX_FIELD_NAME_MUST_BE_UNIQUE'))
         return;
      }
      if (
        hasInvalidMinLength ||
        hasInvalidMaxLength ||
        hasInvalidPrecision ||
        hasInvalidLength ||
        invalidMaxLength ||
        hasInvalidLengthCharLimit ||
        hasInvalidMaxLengthCharLimit ||
        hasInvalidMinLengthCharLimit ||
        hasInvalidPrecisionCharLimit
      ) {
        return;
      } else {
        const position = createNewNodes(rowData, record);
        addNodesToTree(record,position);
      }
    }

    // Function to fetch data from the grid
    function fetchDataFromGrid(rowData) {
      gridApi.forEachNode((node) => {
        rowData.push(node.data);
      });
    }
    function handleInputLimit(fieldName,errorMsg) {
      const isValidInput = (value) => value.length <= 9;
      const hasInvalidValue = rowData.some((record) => {
        if (record[fieldName]) {
          const isValid = isValidInput(record[fieldName]);
          return !isValid;
        }
        return false;
      });

      if (hasInvalidValue) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(errorMsg);
        return true;
      }

      return false;
    }
    function validateNumericField (fieldName, errorMsg) {
      const isValidNumber = (value) => /^\d+$/.test(value);
      const hasInvalidValue = rowData.some((record) => {
        if (record[fieldName]) {
          const isNumeric = isValidNumber(record[fieldName]);
          return !isNumeric;
        }
        return false;
      });

      if (hasInvalidValue) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(errorMsg);
        return true;
      }

      return false;
    };
    function validateMaxLength() {
      const hasInvalidLength = rowData.some((record) => {
        const maxLength = record.maxLength;
        const minLength = record.minLength;
        
        if (maxLength && minLength && Number(maxLength) < Number(minLength)) {
            return true;
        }
    
        return false;
    });
      if(hasInvalidLength) {
        setHasDuplicateFieldName(true);
        setFieldNameDuplicateMsg(t('BOX_MSG_MAX_LENGTH_VALIDATION'))
        return true;
      }
      return false;
    }
  
    function createNewNodes(rowData, record) {
      let pos = [];
      rowData
        .filter((node) => {
           return node.mapName
        }) // Filter out rows without mapName for inbound
        .map((node,key) => {
          const duplicate = checkForDuplicates(node,parentArray)
          if(duplicate.pos === -1){
            pos.push(key);
          }
          if (!duplicate.result) {
            const title = node.description || node.mapName;
            const fieldType =
              node.fieldType === fieldTypeOptionsArray[0].value
                ? "ALPHA"
                : fieldTypeOptionsArray.some((o) => node.fieldType === o.value)
                ? node.fieldType
                : "ALPHA";

            const newNode = {
              nodeId: genKey(node.mapName),
              title: `Field: ${title}`,
              ICON_DISPLAY: DATA_TYPE[templateHeader?.FILETYPE]?.field?.icon,
              type: "FIELD",
              children: [],
              isLeaf: true,
              FIELDTYPE: fieldType,
              ...(currentScreen === 'inbound' && { MAPNAME: node.mapName })

            };
            if (node.description || node.mapName) {
              newNode.DESCRIPTION = node.description || node.mapName;
            }
            if (node.fieldType === "DATE" && node.pattern) {
              newNode.PATTERN = node.pattern;
            }
            if (['FLOAT','INTEGER','PACKED_INT','ZONED_INT'].includes(node.fieldType)) {
              newNode.PRECISION = node.precision || 0;
            }
            if (node.maxLength) {
              newNode.MAX_LENGTH = node.maxLength;
            }
            if (node.minLength) {
              newNode.MIN_LENGTH = node.minLength;
            }
            if (node.req && (node.req =='Y' || node.req === true )) {
              newNode.REQUIRED = true;
            }
            if (node.length) {
              newNode.LENGTH = node.length;
            }
            if (node.hl7DataType && templateHeader.FILETYPE === "HL7") {
              newNode.DATA_TYPE =
                node.hl7DataType === hl7DatatypeDropdownItems[0]
                  ? "ST"
                  : node.hl7DataType;
            }
            if(templateHeader?.FILETYPE === 'DATABASE' && [true,'true','TRUE'].includes(templateHeader.INCLUDE_NULL) ) newNode.INCLUDE_NULL = 'TRUE'
            const formattingOptions = ['TRIMDATA', 'QUOTE_DATA', 'URLENCODE'];
            formattingOptions.forEach(condition => {
                if ([true, 'true', 'TRUE'].includes(templateHeader[condition])) {
                    newNode[condition] = 'TRUE';
                }
            });
            record.push(newNode);
          }
        });
        return pos;
    }

    // Function to check for duplicates
    function checkForDuplicates(node, parentArray) {
      let pos = -1;
      for (let i = 0; i < parentArray.length; i++) {
        if (
          parentArray[i].ICON_DISPLAY === "RECORD" ||
          parentArray[i].ICON_DISPLAY === "RECORD_TOP"
        ) {
          continue;
        }
        if (currentScreen === 'inbound' &&
          node.mapName &&
          node.mapName.toLowerCase() === parentArray[i].MAPNAME?.toLowerCase()
        ) {
          pos = i;
          return {result:true,pos:pos};
        } 
        if(currentScreen === 'outbound' && node.description && node.description.toLowerCase() === parentArray[i]?.DESCRIPTION?.toLowerCase()){
          pos = i;
          return {result:true,pos:pos};
        }
      }
      
      return {result:false,pos:-1};
    }
    function checkMapNameExists(rowData) {
      let mapNameExist = true;
      rowData
        .filter(
          (record) =>
            !(Object.keys(record).length === 1 && record.hasOwnProperty("id"))
        )
        .forEach((record) => {
          const hasEmptyString = Object.keys(record).some((key) => {
            return key !== "id" && record[key] === "";
          });
    
          if (!record.mapName && record.description !== "" && !hasEmptyString) {
            mapNameExist = false;
          }
        });
      return mapNameExist;
    }

    function isUniqueMapNameInGrid(rowData){
      let isUnique = true;
      const mapNameList = new Set();
      rowData.filter(row =>  !(Object.keys(row).length === 1 && row.hasOwnProperty("id"))
      )
      .forEach(row => {
        if(row.mapName){
          if(mapNameList.has(row.mapName.toLowerCase())){
            isUnique = false;
            return;
          }
          mapNameList.add(row.mapName.toLowerCase())
        }
      })
      return isUnique;
    } 
    // Function to add nodes to the tree
    function addNodesToTree(record,position) {
      if (record.length > 0) {
        handleAdd(record, selectedNode, isInside,position);
      }
      toggle();
    }
  };

  const getGridAPI = (api) => {
    setGridApi(api);
  };
  const getDelimiter = (delim) => {
    setDelimiter(delim);
  };

  const handleOKClick = () => {
    setRowData([
      {
        id: 0,
        mapName: "",
        description: "",
        fieldType: fieldTypeOptionsArray[0].text,
        length: "",
      },
    ]);
    setConfirmModal(false);
  };
  const dataFromFile = (data) => {
    if (!data) return;
    setData(data);
    let delim = delimiter;
    if (delim === "CRLF") {
      delim = "\\r\\n";
    }

    const parsedData = data.split(delim);
    const arrayToObject = (parsedData) => {
      return parsedData.map((mapName, index) => ({
        id: rowData.length + index,
        mapName: mapName,
        description: mapName,
        fieldType: "ALPHA",
        hl7DataType: templateHeader.FILETYPE === 'HL7' ? "ST" : undefined
      }));
    };

    const result = arrayToObject(parsedData);
    let gridData = [];
    gridApi.forEachNode((node) => {
      gridData.push(node.data);
    });
    // Filter out rows that have a mapName
    gridData = gridData.filter(
      (row) => row.hasOwnProperty("mapName") && row.mapName !== ""
    );
    // Add the new data to gridData
    gridData.push(...result);

    // Create an empty row
    const emptyRow = {
      id: gridData.length,
      mapName: "",
      description: "",
      fieldType: fieldTypeOptionsArray[0].text,
      length: "",
    };

    // Add the empty row
    gridData.push(emptyRow);

    // Update the rowData state with the updated gridData
    setRowData(gridData);
  };
  const handleBackdropClick = (event, reason) => {
    // Prevent closing the drawer when the backdrop is clicked
    if (reason === "backdropClick") return;
  };
  const handleCustomizeModalClick = () => {
    setCustomizeDialog(true);
  };
  const closeCustomizeModal = () => {
    setCustomizeDialog(false);
  };
  const handleAvailableColumns = (columns) => {
    setAvailableColumns(columns);
  };
  const handleTabData = (tabData) => {
    setTabData(tabData);
  };
  const updateGridOptions = (availableColumns) => {
    const checkboxes = availableColumns;
    let rowData = [];
    gridApi?.forEachNode((node) => {
      rowData.push(node.data);
    });
    setRowData((rowData && rowData.length > 0) ? rowData : [{ id: 0, fieldType: fieldTypeOptionsArray[0].value, description: '', mapName: '' }]);

    // Convert string values to booleans
    const stringToBoolean = () => {
      const convertedCheckboxes = {};
      for (const key in checkboxes) {
        convertedCheckboxes[key] = JSON.parse(checkboxes[key]);
      }
      return convertedCheckboxes;
    };
    const data = stringToBoolean();

    const filterColumns = (checkboxes) => {
      const checkedChkBoxes = {};
      for (const key in checkboxes) {
        if (checkboxes.hasOwnProperty(key) && checkboxes[key] === true) {
          checkedChkBoxes[key] = true;
        }
      }
      return checkedChkBoxes;
    };
    // Get the existing column names from columnDefs
    const existingColumnNames = columnDefs.map((colDef) => colDef.headerName);
    const filteredData = filterColumns(data); // Filter those that are checked in customize window
    if (data) {
      // Create a filtered list of column names to remove
      const columnsToRemove = [];
      for (const column in data) {
        if (data[column] === false) {
          columnsToRemove.push(columnMapping[column]);
        }
        if (
          column === "hl7DataType" &&
          data[column] === true &&
          templateHeader.FILETYPE !== "HL7"
        ) {
          columnsToRemove.push(columnMapping[column]);
        }
      }
      // Filter the existing column definitions to remove columns
      const filteredColumnDefs = columnDefs.filter((colDef) => {
        return !columnsToRemove.includes(colDef.headerName);
      });
      // Update the columnDefs with the filtered columns
      setColumnDefs(filteredColumnDefs);

      // Add new columns for any columns that are checked
      const newColumnDefs = [];
      const numericColumnWidth = {
        length: 110,
        maxLength: 180,
        minLength: 170,
        precision: 120,
      };
      for (const column in filteredData) {
        // Check if the column name already exists in the grid
        if (!existingColumnNames.includes(columnMapping[column])) {
          const columnDef = {
            headerName: columnMapping[column],
            field: column,
            resizable: true,
            editable: columnMapping[column] === t("LBL_PATTERN") ? false : true,
            showPlaceholder: false,
            lockVisible:true
          };

          // Add a custom cell renderer for the "req" column
          if (
            column === "req" ||
            column === "trimData" ||
            column === "quoteData" ||
            column === "urlEncodeData" ||
            column === "suppressFieldDelimiter"
          ) {
            columnDef.cellRenderer = CustomCheckBoxEditor;
            columnDef.width = 120;
          }
          if (
            column === "length" ||
            column === "maxLength" ||
            column === "minLength" ||
            column === "precision" 
            ) {
            columnDef.cellRenderer = CustomNumericCellEditor;
            columnDef.width = numericColumnWidth[column]
          }
          if (column === "pattern") {
            columnDef.cellRenderer = PatternCellEditor;
            columnDef.width = 120
          }
          if (column === "hl7DataType") {
            columnDef.cellRenderer = HL7DataTypeDropdown;
            columnDef.cellRendererParams = { values: hl7DatatypeDropdownItems };
            columnDef.width = 150
          }
          if(column === "constVal" || column === 'addToWorkflowSession'){
            columnDef.cellRenderer = CustomTextInputRenderer
          }
          if(column === 'convertCase'){
            columnDef.cellRenderer = CustomDropdownEditor
            columnDef.cellRendererParams = { values: convertCaseDropdownItems}
          }
          if(column === 'fillCharacter'){
            columnDef.cellRenderer = CustomAutocompleteCellRenderer
            columnDef.cellRendererParams = { values: fillCharacterDropdownItems}
          }
          if(column === 'paddingDirection'){
            columnDef.cellRenderer = CustomDropdownEditor
            columnDef.cellRendererParams = { values: paddingDirectionDropdownItems}
          }
          newColumnDefs.push(columnDef);
        }
      }
      if (newColumnDefs.length > 0) {
        // Update the columnDefs by adding the new columns
        setColumnDefs((prevColumnDefs) => [
          ...prevColumnDefs,
          ...newColumnDefs,
        ]);
      }
    }
  };

  const handleCustomizePasteLogic = (customizePasteLog = {}) => {
    setCustomizePasteLogic((prev) => {
      return { ...prev, ...customizePasteLog };
    });
  };
  const handleUpdate = (update) => {
    setPasteLogicUpdated(update)
  }
  const pasteValidator = (data = []) => {
    const getMappedValue = (value, mappingArray = [], key) => {
      const KEYMAPPING = {
        required :"chkVal",
        pattern : "pattern",
        fieldType: "fieldType",
        constVal: "constVal",
      }
      for (let i = 0; i < mappingArray?.length; i++) {
        const val = mappingArray[i]?.value;
        if (value.startsWith(val)) return mappingArray?.[i]?.[KEYMAPPING[key]];
        // if (val === value) return mappingArray?.[i]?.[KEYMAPPING[key]];
      }
      return value;
    };
    function setDropdownValueFromPaste(valTochk, dropdown) {
      for (let option of dropdown) {
        if (option.value.toLowerCase().indexOf(valTochk.toLowerCase()) !== -1 || option.text.toLowerCase().indexOf(valTochk.toLowerCase()) !== -1) {
            return option.value;
        }
    }
    return null;
  }

    const newData = data?.map((e) => {
      const keys = Object.keys(e || {});
      const newRowData = {};
      keys.forEach((o) => {
        const key = (o === "req" || o === "trimData" || o === "quoteData" || o === "suppressFieldDelimiter" || o === "urlEncodeData") ? "required" : o;
        if (tabData?.[key]?.length)
          newRowData[o] = getMappedValue(e?.[o], tabData?.[key], key);
        else newRowData[o] = e?.[key];
      });
      if (e?.pattern !== newRowData.pattern && e.hasOwnProperty('fieldType')) newRowData['fieldType'] = 'DATE';
      if (e.hasOwnProperty('constVal') && e.constVal !== "") newRowData['fieldType'] = 'CONSTANT';
      if(e.hasOwnProperty('convertCase') && e.convertCase !== ""){
       const value = setDropdownValueFromPaste(e.convertCase,convertCaseDropdownItems)
       newRowData['convertCase'] = value
      }
      if(e.hasOwnProperty('paddingDirection') && e.paddingDirection !== ""){
        const value = setDropdownValueFromPaste(e.paddingDirection,paddingDirectionDropdownItems)
        newRowData['paddingDirection'] = value
       }
       if(e.hasOwnProperty('convertCase') && e.convertCase !== ""){
        const value = setDropdownValueFromPaste(e.convertCase,convertCaseDropdownItems)
        newRowData['convertCase'] = value
       }
      return newRowData;
    });
    // setRowData(newData);
    setenableResetGrid(true)
    return newData;
  };
  
  const onDragStopped = useCallback((params) => {
    const newColumnOrder = params?.api?.getColumnState() || [];
    const simplifiedColumnOrder = newColumnOrder.map(({ colId, width }) => ({
      colId,
      width,
    }));

    if (simplifiedColumnOrder) {
      dispatch(
        api.endpoints.postQuickAddColumnOrder.initiate(
          {
            data: { column_order: simplifiedColumnOrder },
          },
          { forceRefetch: true }
        )
      ).then((response) => {
        if (response.data.current_state === "ERROR") {
          console.log("Request couldn't be completed at the moment")
        }
      }).catch((error) => {
        console.error("Failed to dispatch:", error);
      });
    }
  }, []);


    return (
    <>
      <Drawer
        anchor="left"
        open={openModal}
        onClose={handleCancelClick}
        ModalProps={{
          onBackdropClick: handleBackdropClick,
        }}
        sx={{
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: "100%",
            boxSizing: "border-box",
            background: "transparent",
          },
        }}>
        <Main open={openFileModal || customizeDialog}>
          <Split
            
            className="split height-100 flex"
            direction="horizontal"
            gutterSize={4}
            snapOffset={0}
            dragInterval={4}
            minSize={[550, 0]}
            sizes={[67, 33]}
            children={[<div
              className="flex-column height-100"
              key="main"
              style={{ backgroundColor: "#fff", position: "relative" }}>
              <div className="quick-add-container" key={0}>
                <div style={{ lineHeight: "25px" }}>
                  <p style={{ marginBottom: "10px" }}>
                    {t("DIAG_QUICK_FIELD_ADD_TIT")}
                  </p>
                  <div className="font-12">
                    <p>{title[0]}</p>
                    <p>{title[1]}</p>
                  </div>
                </div>

                <IconButton
                  onClick={handleCancelClick}
                  sx={{ borderRadius: "5px" }}>
                  <CloseIcon
                    sx={{
                      height: "28px",
                      width: "28px",
                      color: "#777777",
                    }}
                  />
                </IconButton>
              </div>

              <div
                className="font-12"
                key={1}
                style={{
                  padding: "8px",
                }}>
                <p>{t("LBL_QUICK_FIELD_ADD_NOTE")}</p>
              </div>
              <div className="quick-add-right-container" key={2}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}>
                  <LightTooltip title={t("LBL_CUSTOMIZE_THIS_SCREEN")} arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ marginRight: "10px" }}
                      onClick={handleCustomizeModalClick}>
                      {t('LBL_CUSTOMIZE')}
                    </Button>
                  </LightTooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ marginRight: "10px" }}
                    disabled={!enableResetGrid}
                    onClick={() => setConfirmModal(true)}>
                    {t('LBL_CLEAR_GRID')}
                  </Button>
                </div>
                <CommonGrid
                  contextMenuId="quick-add-target-id"
                  pasteValidator={pasteValidator}
                  identifier="Quick Add"
                  fieldDelimeter={
                    tabData?.delimiter?.fieldDelimiter
                  }
                  delimeter={tabData?.delimiter?.recordDelimter}
                  showContextMenu={true}
                  fullSize={false}
                  isLoading={isLoading}
                  key={gridKey}
                  rowData={rowData}
                  gridOptions={gridOptions}
                  containerClasses={"quick-add-data-grid-container"}
                  getGridAPI={getGridAPI}
                  gridProps={{
                    suppressScrollOnNewData: true,
                    suppressScrollWhenPopupsAreOpen: true,
                    frameworkComponents: {
                      customDropdownEditor: CustomDropdownEditor,
                      customNumericCellEditor:CustomNumericCellEditor,
                      customTextInputRenderer:CustomTextInputRenderer,
                      customAutocompleteCellRenderer: CustomAutocompleteCellRenderer
                    },
                    animateRows: false,
                    onCellValueChanged: handleCellValueChanged,
                    enableCellTextSelection: true,
                    ensureDomOrder: true,
                    suppressClickEdit: true,
                    onDragStopped: onDragStopped,
                  }}
                  avoidMultipleRowsValidation={true}
                />
                <Box>
                  <Divider></Divider>
                  <div
                    className="margin-top-4"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}>
                    <LightTooltip
                      title={t('ADD_FROM_FILE_TITLE')}
                      arrow>
                      <Button
                        size="small"
                        variant="outlined"
                        style={{ marginRight: "10px" }}
                        onClick={() => setOpenFileModal(true)}>
                        {t("BTN_ADD_FROM_FILE")}
                      </Button>
                    </LightTooltip>
                    <div>
                      { currentScreen === 'inbound' && (
                      <LightTooltip
                        title={t('ADD_NEW_TITLE')}
                        arrow>
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ marginRight: "10px" }}
                          onClick={handleAddNew}>
                          {t("BTN_ADD_NEW")}
                        </Button>
                      </LightTooltip>)}
                      <LightTooltip
                        title={t('ADD_FIELD_TITLE')}
                        arrow>
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ marginRight: "10px" }}
                          onClick={handleAddFields}>
                          {t("BTN_ADD")}
                        </Button>
                      </LightTooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCancelClick}>
                        {t("CANCEL")}
                      </Button>
                    </div>
                  </div>
                </Box>
              </div>
            </div>,
            <div key="supportingDiv" style={{background : 'transparent'}}></div>
          ]}
          >
              
          </Split>
        </Main>
      </Drawer>
      {hasDuplicateFieldName ? (
        <Dialog open={hasDuplicateFieldName} maxWidth="xs">
          <DialogTitle className="dialog-header">
            <div className="title">{t("ERROR")}</div>
          </DialogTitle>
          <DialogContent className="modal-content" dividers>
            <div>{fieldNameDuplicateMsg}</div>
          </DialogContent>
          <DialogActions>
            <Button
              size='small'
              variant="outlined"
              color="primary"
              onClick={() => {
                setHasDuplicateFieldName(false);
              }}>
              {t("BTN_OK")}
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        ""
      )}
      {openFileModal ? (
        <div style={{ transition: "width 0.7s" }}>
          <QuickAddFromFile
            open={openFileModal}
            setOpenFileModal={setOpenFileModal}
            setData={dataFromFile}
            delimiter={delimiter}
            onDelimiterChange={getDelimiter}
          />
        </div>
      ) : (
        ""
      )}
      {confirmModal ? (
        <ErrorModal
          errorModal={confirmModal}
          errorMessage={"Are you sure , you want to clear the grid ?"}
          errorType={t("BOX_CONFIRM")}
          handleErrorModal={() => setConfirmModal(false)}
          OkHandler={handleOKClick}></ErrorModal>
      ) : (
        ""
      )}

      {customizeDialog ? (
        <CustomizeQuickFieldAdd
          quickAddModal={openModal}
          open={customizeDialog}
          setOpenCustomizeModal={closeCustomizeModal}
          columns={availableColumns}
          tabData={tabData}
          setAvailableColumns={handleAvailableColumns}
          setTabData={handleTabData}
          handleCustomizePasteLogic={handleCustomizePasteLogic}
          setPasteLogicUpdated={handleUpdate}
        />
      ) : (
        ""
      )}
    </>
  );
}

export default QuickAddModal;
